"use client";

import React, { forwardRef, useCallback, useMemo } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { MediaSetContext } from "@perceptron/utils/types";
import type { EditorProps } from "@tiptap/pm/view";
import { useEditor, Editor, EditorContent } from "@tiptap/react";

import { useEditorExtensions } from "@demo/extensions";
import { CaretBubblePosition } from "@demo/extensions/caret-bubble";
import {
  useEditorFocus,
  useEditorCommands,
  useEditorDirtyState,
  useEditorRegistration,
  useContentChangeNotification,
  useDocumentContent,
} from "@demo/hooks/editor";
import { isMobileViewport } from "@demo/hooks/useIsMobile";
import { useTiptapZustandSync } from "@demo/hooks/useTiptapZustandSync";
import { useCaretBubbleStore } from "@demo/store/caret-bubble";
import { Role } from "@demo/types/base";

import { promptClipboardText } from "./clipboardText";
import { EditorContainer } from "./Container";
import { EditorLoadingSkeleton } from "./LoadingSkeleton";
import EditorUI from "./Overlay";

export interface DocumentEditorProps {
  id?: string;
  type: Role;
  placeholder?: string;
  mobilePlaceholder?: string;
  onHasEditorContent?: (hasContent: { text: boolean; media: boolean }) => void;
}

export interface DocumentEditorRef {
  editor: Editor | null;
  clear: () => void;
  focus: () => void;
  getEditor: () => Editor | null;
  handleFileDrop: (files: File[]) => void;
}

const resolvePlaceholderText = (type: Role, placeholder?: string, mobilePlaceholder?: string): string => {
  if (isMobileViewport()) {
    if (mobilePlaceholder) return mobilePlaceholder;
    return type === Role.USER
      ? strings.editor.placeholders.demoMobileUserEditor
      : strings.editor.placeholders.demoMobileSystemEditor;
  }

  if (placeholder) return placeholder;

  return type === Role.USER ? strings.editor.placeholders.demoUserEditor : strings.editor.placeholders.demoSystemEditor;
};

/**
 * One object for the editor's life: useEditor re-applies options whose identity changes, and a new object
 * per render sent the editor into an update loop. Copying as plain text leaves images out rather than
 * turning each into a blank gap.
 */
const EDITOR_PROPS: EditorProps = {
  clipboardTextSerializer: (slice, view) => promptClipboardText(slice, view.state.schema),
};

const DocumentEditor = forwardRef<DocumentEditorRef, DocumentEditorProps>(
  ({ id, type, placeholder, mobilePlaceholder, onHasEditorContent }, ref) => {
    // Caret bubble store subscriptions
    const activeEditor = useCaretBubbleStore((state) => state.activeEditor);
    const caretPosition = useCaretBubbleStore((state) => state.caretPosition);
    const setCaretPosition = useCaretBubbleStore((state) => state.setCaretPosition);

    // Memoize the caret position change handler to prevent unnecessary re-renders
    const handleCaretPositionChange = useCallback(
      (position: CaretBubblePosition) => {
        if (position.isValidTarget) {
          setCaretPosition(type, position);
        } else {
          // Clear bubble if position is not valid
          // Get activeEditor from store directly to avoid stale closure
          const currentActiveEditor = useCaretBubbleStore.getState().activeEditor;
          if (currentActiveEditor === type) {
            setCaretPosition(null, null);
          }
        }
      },
      [type, setCaretPosition],
    );

    const extensions = useEditorExtensions({
      context: MediaSetContext.DOCUMENT_EDITOR,
      placeholder: resolvePlaceholderText(type, placeholder, mobilePlaceholder),
      role: type,
      onCaretPositionChange: handleCaretPositionChange,
    });

    // Whether the bubble is in the DOM (reachable with Tab), and whether it's shown to the pointer (mouse proximity)
    const isBubbleAvailable = useCaretBubbleStore((state) => state.isAvailableForEditor(type, false));
    const shouldShowBubble = useCaretBubbleStore((state) => state.isVisibleForEditor(type, false));

    // Check if this editor is active
    const isActiveEditor = activeEditor === type;

    // Document content
    const { draftContent, savedDocumentContent, activeDocumentUuid, isLoadingDocument } = useDocumentContent(type);

    // Initialize editor
    const editor = useEditor({
      extensions,
      content: draftContent,
      autofocus: false,
      immediatelyRender: false,
      editorProps: EDITOR_PROPS,
      onCreate: () => {
        focusEditor();
      },
    });

    // Synchronization (debounced editor -> store draft sync + store -> editor echo)
    useTiptapZustandSync(editor, activeDocumentUuid, type, isLoadingDocument);

    // Custom hooks
    const { focusEditor } = useEditorFocus({
      editor,
      editorRole: type,
    });

    const { commands } = useEditorCommands({
      editor,
      editorRole: type,
    });

    // Track whether the editor content differs from the last saved/run content.
    // The flag lives in the editor-ref store so a sync-status indicator can read
    // it; this hook just keeps it current.
    useEditorDirtyState({
      editor,
      editorRole: type,
      savedDocumentContent,
      isLoadingDocument,
    });

    const editorCommands = useMemo(
      () => ({
        focus: () => commands.focus("end"),
        clear: commands.clear,
        getEditor: () => editor,
        handleFileDrop: (files: File[]) => {
          if (editor) {
            // Focus the editor first
            commands.focus("end");
            // Get the current cursor position
            const pos = editor.state.selection.head;
            // Find the FileHandler extension and trigger its onDrop handler
            const extensions = editor.extensionManager.extensions;
            const fileHandler = extensions.find((ext) => ext.name === "fileHandler");
            if (fileHandler && fileHandler.options.onDrop) {
              fileHandler.options.onDrop(editor, files, pos);
            }
          }
        },
      }),
      [commands, editor],
    );

    // Effects
    useEditorRegistration(editor, type, ref, editorCommands);
    useContentChangeNotification(editor, onHasEditorContent);

    // Don't render until editor is initialized
    if (!editor) {
      return (
        <EditorContainer className={cn(`editor-role-${type.toLowerCase()}`)} isLoading={true}>
          <EditorLoadingSkeleton />
        </EditorContainer>
      );
    }

    return (
      <EditorContainer
        className={cn(shouldShowBubble ? "caret-bubble-visible" : "", `editor-role-${type.toLowerCase()}`)}
        isLoading={!!isLoadingDocument}
      >
        <EditorContent className="editor-content min-h-0 flex-1" editor={editor} id={id} />

        {!!isLoadingDocument && <EditorLoadingSkeleton />}

        <EditorUI
          caretPosition={isActiveEditor ? caretPosition : null}
          editor={editor}
          isAvailable={isBubbleAvailable}
          isVisible={shouldShowBubble}
          role={type}
        />
      </EditorContainer>
    );
  },
);

DocumentEditor.displayName = "DocumentEditor";
export default DocumentEditor;
